import React from "react";
import styled from "styled-components";
import { FaBlog, FaChevronDown, FaCompass, FaDownload, FaUsers } from "react-icons/fa"; // Importing the downward arrow icon
import { FaDog, FaCat, FaDove, FaRabbit, FaFish, FaHamster } from "react-icons/fa"; // Importing icons for pet types
import { BiChevronDown, BiDownload, BiNews } from "react-icons/bi";
import { SiDiscover } from "react-icons/si";
import { FiCompass } from "react-icons/fi";
import { BsCompass } from "react-icons/bs";
import { GrCompass } from "react-icons/gr";

const Section = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem 0;
  background-color: #f0f4f4; /* Light gray background */
  color: #333; /* Dark gray text color for better contrast */
`;

const Container = styled.div`
  width: 100%;
  max-width: 1400px;
  display: flex;
  justify-content: space-between; /* Space between logo, nav, and button */
  align-items: center;
  padding: 0 1.5rem;
  position: relative; /* Enables absolute positioning for centering */
`;

const NavCenter = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: transform 0.3s;
  cursor: pointer;

  &:hover {
    transform: scale(1.05);
  }
`;

const PictureFrame = styled.div`
  height: 60px;
  width: 60px;
  border-radius: 50%;
  // background-color: #e4f4f4; /* Lighter teal background */
  // border: 2px solid #66b2b2; /* Lighter teal color for the border */
  overflow: hidden;
  position: relative;
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 51%;
  left: 50%;
  transform: translate(-50%, -50%) scale(1.25);
`;

const LogoText = styled.a`
  text-decoration: none;
  color: #66b2b2; /* Light teal */
  font-size: 1.5rem;
  font-weight: bold;
  position: relative; /* Needed for the dropdown to position itself */
  padding: 0.5rem;
  display: flex;
  align-items: center;
  margin-left: -15px;

  &:hover {
    color: #80c8c8; /* Lighter teal on hover */
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem; /* Add space between each navigation link */
`;

const NavLink = styled.div`
  text-decoration: none;
  color: #66b2b2;
  font-size: 1.2rem;
  font-weight: normal;
  padding: 0.5rem;
  transition: transform 0.3s;
  cursor: pointer;

  &:hover {
    color: #80c8c8; /* Lighter teal on hover */
    transform: scale(1.05);
  }
`;

const DropdownContainer = styled.div`
  display: none; /* Hidden by default */
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #e4f4f4;
  border: 2px solid #66b2b2;
  border-radius: 4px;
  padding: 0.5rem;
  z-index: 10;
  width: 150px; /* Smaller dropdown */
  box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.1);
`;

const DropdownItem = styled.a`
  display: flex;
  align-items: center;
  color: #66b2b2;
  padding: 0.5rem 0; /* Padding for top and bottom */
  padding-left: .5rem; /* Add padding to the right */
  font-size: 1rem;
  text-decoration: none;
  margin: 4px;

  &:hover {
    background-color: #66b2b2; /* Light teal on hover */
    color: white;
    border-radius: 10px;
  }

  svg {
    margin-right: 0.5rem; /* Space between icon and text */
  }
`;

const SignInButton = styled.button`
  background-color: #66b2b2;
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  font-size: 1rem;
  border-radius: 10px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: transform 0.3s, background-color 0.3s;

  &:hover {
    transform: scale(1.05);
    background-color: #80c8c8;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 200%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.5s;
  }

  &:hover::after {
    left: 100%;
  }
`;

const DiscoverContainer = styled.div`
  position: relative;

  &:hover ${DropdownContainer} {
    display: block; /* Show dropdown when hovering over the "Discover Pets" link */
  }
`;

const Chevron = styled(BiChevronDown)`
  margin-left: 0.0rem; /* Space between chevron and text */
  vertical-align: middle; /* Align the chevron vertically */
  margin-bottom: 1px; /* Adjusts the vertical position of the chevron */
`;

const Download = styled(BiDownload)`
  margin-right: 0.3rem; /* Space between chevron and text */
  vertical-align: middle; /* Align the chevron vertically */
  margin-bottom: 3.5px; /* Adjusts the vertical position of the chevron */
`;

const Discover = styled(GrCompass)`
  margin-right: 0.3rem; /* Space between chevron and text */
  vertical-align: middle; /* Align the chevron vertically */
  margin-bottom: 2.5px; /* Adjusts the vertical position of the chevron */
`;

const Newsletter = styled(BiNews)`
  margin-right: 0.3rem; /* Space between chevron and text */
  vertical-align: middle; /* Align the chevron vertically */
  margin-bottom: 3px; /* Adjusts the vertical position of the chevron */
`;

const Community = styled(FaUsers)`
  margin-right: 0.3rem; /* Space between chevron and text */
  vertical-align: middle; /* Align the chevron vertically */
  margin-bottom: 3px; /* Adjusts the vertical position of the chevron */
`;

function Navbar() {
  return (
    <Section>
  <Container>
    {/* Logo for FurEverPixels */}
    <LogoContainer>
      <PictureFrame>
        <LogoImage src="assets/icon.png" alt="FurEverPixels Logo" />
      </PictureFrame>
      <LogoText href="/">FurEverPixels</LogoText>
    </LogoContainer>

    {/* Centered Navigation Link */}
    <NavCenter>
      <NavLinks>
      <DiscoverContainer>
        <NavLink href="#">
        <Discover size={18} />
          Discover pets
          <Chevron size={24} />
        </NavLink>
        <DropdownContainer>
          <DropdownItem href="#">
            <FaDog size={18} /> Dogs
          </DropdownItem>
          <DropdownItem href="#">
            <FaCat size={18} /> Cats
          </DropdownItem>
          <DropdownItem href="#">
            <FaDove size={18} /> Birds
          </DropdownItem>
          <DropdownItem href="#">
          <FaDove size={18} /> Rabbits
          </DropdownItem>
          <DropdownItem href="#">
          <FaDove size={18} /> Hamsters
          </DropdownItem>
          <DropdownItem href="#">
            <FaFish size={18} /> Fish
          </DropdownItem>
        </DropdownContainer>
      </DiscoverContainer>
      <NavLink href="#">
      <Newsletter size={18} />
          The Paw Print
        </NavLink>
        <NavLink href="/community">
  <Community size={18} />
  Community
</NavLink>
      <NavLink href="#">
      <Download size={18} />
          Get App
        </NavLink>
      </NavLinks>
    </NavCenter>

    {/* Sign in Button */}
    <SignInButton>Sign in</SignInButton>
  </Container>
</Section>
  );
}

export default Navbar;
